import { useCallback, useEffect, useRef } from 'react';

import { useSelector } from 'react-redux';

import { StreamTypes } from 'store/player/actions.types';
import { selectStandardChannelById } from 'store/programs/selectors';
import { ReduxStore } from 'store/types';

import parseVideoItem from 'services/data-manager/parsing/parseVideoItem';
import { VideoItem } from 'services/data-manager/types';

import usePlayerState from 'components/EmbeddedPlayer/hooks/usePlayerState';

export default function useVideoItem(
    originalVideoItem: VideoItem | undefined,
    originalPlaybackType: StreamTypes | undefined,
) {
    const channel = useSelector((state: ReduxStore) =>
        selectStandardChannelById(state, originalVideoItem?.channelId),
    );

    const {
        playerState: { videoItem, playbackType },
        setPlayerState,
    } = usePlayerState({
        videoItem: originalVideoItem,
        playbackType: originalPlaybackType,
    });

    const prevOriginalVideoItem = useRef<VideoItem | undefined>(
        originalVideoItem,
    );

    const handleResetVideoItem = useCallback(() => {
        setPlayerState({
            videoItem: undefined,
            playbackType: undefined,
        });
    }, [setPlayerState]);

    useEffect(() => {
        const program = originalVideoItem?.originalProgram;
        const searchVideoItem = originalVideoItem?.contentId;

        if (
            originalVideoItem !== prevOriginalVideoItem.current &&
            !program &&
            !searchVideoItem
        ) {
            if (channel) {
                setPlayerState({
                    videoItem: parseVideoItem({ channel, program: undefined }),
                    playbackType: originalPlaybackType,
                });
            }
        } else {
            setPlayerState({
                videoItem: originalVideoItem,
                playbackType: originalPlaybackType,
            });
        }

        prevOriginalVideoItem.current = originalVideoItem;
    }, [originalVideoItem, originalPlaybackType, prevOriginalVideoItem]);

    return {
        videoItem,
        playbackType,
        onResetVideoItem: handleResetVideoItem,
    };
}
